<template>
  <div v-if="!layouts.length">
    <ReportEmptyState
      title="Nenhum layout cadastrado"
      description="Crie modelos visuais para controlar a aparência dos relatórios técnicos."
      :icon="PhSlidersHorizontal"
    />
  </div>

  <div v-else class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
    <article
      v-for="layout in layouts"
      :key="layout.id"
      class="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm transition hover:border-green-100 hover:shadow-md"
    >
      <div class="flex items-start justify-between gap-3">
        <div>
          <div class="flex flex-wrap items-center gap-2">
            <h3 class="font-semibold leading-tight text-gray-800">
              {{ layout.name }}
            </h3>

            <span
              v-if="layout.is_default"
              class="inline-flex items-center gap-1 rounded-full bg-yellow-50 px-2.5 py-1 text-xs font-semibold text-yellow-700"
            >
              <PhStar class="h-3.5 w-3.5" />
              Padrão
            </span>
          </div>

          <p class="mt-1 text-xs text-gray-500">
            {{ layout.description || "Sem descrição" }}
          </p>
        </div>

        <span
          class="inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-xs font-semibold"
          :class="
            layout.is_active
              ? 'bg-green-50 text-green-700'
              : 'bg-gray-100 text-gray-500'
          "
        >
          {{ layout.is_active ? "Ativo" : "Inativo" }}
        </span>
      </div>

      <div
        class="mt-5 flex h-40 items-center justify-center overflow-hidden rounded-2xl border border-gray-100 bg-gray-50"
      >
        <img
          v-if="getAssetUrl(layout, 'cover')"
          :src="getAssetUrl(layout, 'cover')"
          :alt="layout.name"
          class="h-full w-full object-cover"
        />

        <div v-else class="text-center">
          <PhSlidersHorizontal class="mx-auto h-10 w-10 text-gray-300" />
          <p class="mt-2 text-xs text-gray-400">Sem capa</p>
        </div>
      </div>

      <div class="mt-5 rounded-2xl border border-gray-100 bg-gray-50/70 p-4">
        <div class="flex items-center justify-between text-sm">
          <span class="text-gray-400">Formato</span>
          <span class="font-medium text-gray-700">
            {{ getFormatLabel(layout.page_format) }} /
            {{ getOrientationLabel(layout.orientation) }}
          </span>
        </div>

        <div class="mt-4 flex flex-wrap gap-1.5">
          <span
            v-for="file in getFileBadges(layout)"
            :key="file.label"
            class="rounded-full px-2.5 py-1 text-xs font-semibold"
            :class="
              file.active
                ? 'bg-green-50 text-green-700'
                : 'bg-white text-gray-400'
            "
          >
            {{ file.label }}
          </span>
        </div>
      </div>

      <div class="mt-5 grid grid-cols-2 gap-3 text-sm">
        <div>
          <p class="text-gray-400">Cor principal</p>
          <div class="mt-1 flex items-center gap-2">
            <span
              class="h-5 w-5 rounded-full border border-gray-100"
              :style="{ backgroundColor: layout.primary_color || '#2f8f2f' }"
            ></span>
            <span class="font-medium text-gray-700">
              {{ layout.primary_color || "#2f8f2f" }}
            </span>
          </div>
        </div>

        <div>
          <p class="text-gray-400">Cor secundária</p>
          <div class="mt-1 flex items-center gap-2">
            <span
              class="h-5 w-5 rounded-full border border-gray-100"
              :style="{ backgroundColor: layout.secondary_color || '#5dbb45' }"
            ></span>
            <span class="font-medium text-gray-700">
              {{ layout.secondary_color || "#5dbb45" }}
            </span>
          </div>
        </div>
      </div>

      <div
        class="mt-5 flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 pt-4"
      >
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-gray-500 transition hover:bg-green-50 hover:text-green-700"
          @click="$emit('configure', layout)"
        >
          <PhSlidersHorizontal class="h-4 w-4" />
          Configurar
        </button>

        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-gray-500 transition hover:bg-yellow-50 hover:text-yellow-600"
          @click="$emit('edit', layout)"
        >
          <PhNotePencil class="h-4 w-4" />
          Editar
        </button>

        <button
          v-if="layout.is_active && !layout.is_default"
          type="button"
          class="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-gray-500 transition hover:bg-yellow-50 hover:text-yellow-700"
          @click="$emit('set-default', layout)"
        >
          <PhStar class="h-4 w-4" />
          Padrão
        </button>

        <button
          v-if="layout.is_active"
          type="button"
          class="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-gray-500 transition hover:bg-red-50 hover:text-red-600"
          @click="$emit('disable', layout)"
        >
          <PhPower class="h-4 w-4" />
          Inativar
        </button>

        <button
          v-else
          type="button"
          class="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-gray-500 transition hover:bg-green-50 hover:text-green-700"
          @click="$emit('enable', layout)"
        >
          <PhCheckCircle class="h-4 w-4" />
          Reativar
        </button>
      </div>
    </article>
  </div>
</template>

<script setup>
import {
  PhCheckCircle,
  PhNotePencil,
  PhPower,
  PhSlidersHorizontal,
  PhStar,
} from "@phosphor-icons/vue";

import ReportEmptyState from "../shared/ReportEmptyState.vue";

defineProps({
  layouts: {
    type: Array,
    default: () => [],
  },
});

defineEmits(["configure", "edit", "disable", "enable", "set-default"]);

function getFormatLabel(format) {
  const labels = {
    a4: "A4",
    letter: "Carta",
  };

  return labels[format] || "A4";
}

function getOrientationLabel(orientation) {
  const labels = {
    portrait: "Retrato",
    landscape: "Paisagem",
  };

  return labels[orientation] || "Retrato";
}

function getFileBadges(layout) {
  return [
    {
      label: "Capa",
      active: Boolean(layout.cover_url || layout.cover_path),
    },
    {
      label: "Cabeçalho",
      active: Boolean(layout.header_url || layout.header_path),
    },
    {
      label: "Rodapé",
      active: Boolean(layout.footer_url || layout.footer_path),
    },
    {
      label: "Marca d’água",
      active: Boolean(layout.watermark_url || layout.watermark_path),
    },
  ];
}

function getAssetUrl(layout, type) {
  const url = layout[`${type}_url`];

  if (url) return url;

  const path = layout[`${type}_path`];

  if (!path) return "";

  const baseUrl =
    process.env.VUE_APP_FILES_URL || process.env.VUE_APP_BASE_FILES_URL || process.env.VUE_APP_IMAGE_SERVE || "";

  if (!baseUrl) return "";

  return `${baseUrl.replace(/\/$/, "")}/${String(path).replace(/^\//, "")}`;
}
</script>
